import { UnitsApiService } from '@api_patient/units_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import { isAbsolute } from '@module/is_absolute';
import {
	type ClientConditionApiModel,
	client as clientV2,
	ExecuteActionStatus,
	NavigationService,
	StartNavigateRequest,
} from '@visiba/api-vcs-client-v2';
import { Injectable, Scopes } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { safeNumberParse } from '@visiba/std';
import dayjs from 'dayjs';

export type CareNavigationRespondentDecision = 'show-above-110-dialog' | 'reason-for-contact' | 'describe-matter';

export interface CareNavigationStartPayload {
	informationGatheringSubmissionId?: string;
	descriptionOfMatter: string;
	reasonForContact: ClientConditionApiModel | null;
	sourceButtonId: number | null;
	asUnverifiedRepresentativeForNin: string | null;
}

export interface CareNavigationStartResult {
	/** Null when the action completed successfully without producing a deeplink, e.g. an autoreply-only messaging action. */
	deepLink: string | null;
	isAbsolute: boolean;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class CareNavigationService {
	private readonly logger = new Logger('CareNavigationService');
	private readonly routeBase: string;
	private readonly receptionId: number;
	private readonly SESSION_STORAGE_KEY = 'careNavigation_patientNin';
	private readonly DATE_OF_BIRTH_SESSION_STORAGE_KEY = 'careNavigation_patientDateOfBirth';
	private readonly SUBJECT_CONTEXT_SEARCH_PARAMETER = 'careNavigationSubjectContext';

	constructor(
		private readonly unitService: UnitService,
		private readonly universalConfig: UniversalConfig,
		private readonly unitsApiService: UnitsApiService,
		private readonly analyticService: AnalyticService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.routeBase = unit.routeWithoutLicenceholder;
		this.receptionId = unit.receptionId;
	}

	public trackPageView() {
		this.analyticService.trackPageView({ customTitle: 'Care navigation' });
	}

	public getRouteBase() {
		return this.routeBase;
	}

	public getReceptionId() {
		return this.receptionId;
	}

	public getReasonForContactRoute() {
		return getAbsoluteRoute([this.routeBase, 'navigation', 'reason-for-contact'].filter(Boolean).join('/'));
	}

	public setCurrentPatientNin(nin: string | null): void {
		if (nin === null) {
			sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
			sessionStorage.removeItem(this.DATE_OF_BIRTH_SESSION_STORAGE_KEY);
			return;
		}

		sessionStorage.setItem(this.SESSION_STORAGE_KEY, nin);
	}

	public getCurrentPatientNin(): string | null {
		return sessionStorage.getItem(this.SESSION_STORAGE_KEY) ?? null;
	}

	/**
	 * An unverified subject's identity number may not encode a date of birth. When Red Robin
	 * redirects such a subject to Care Navigation, preserve the entered date of birth so the
	 * navigation request can still include an age. The backend resolves age for all other subjects.
	 */
	public setCurrentPatientDateOfBirth(dateOfBirth: string | null): void {
		if (dateOfBirth === null) {
			sessionStorage.removeItem(this.DATE_OF_BIRTH_SESSION_STORAGE_KEY);
			return;
		}

		sessionStorage.setItem(this.DATE_OF_BIRTH_SESSION_STORAGE_KEY, dateOfBirth);
	}

	public getCurrentPatientAge(): number | null {
		const storedDateOfBirth = sessionStorage.getItem(this.DATE_OF_BIRTH_SESSION_STORAGE_KEY);
		return storedDateOfBirth !== null ? dayjs().diff(dayjs(storedDateOfBirth), 'years') : null;
	}

	public getDescribeMatterRoute(options: { preserveSubjectContext?: boolean } = {}) {
		const url = new URL(
			getAbsoluteRoute([this.routeBase, 'navigation', 'describe-your-matter'].filter(Boolean).join('/')),
			window.location.origin,
		);
		if (options.preserveSubjectContext) {
			url.searchParams.set(this.SUBJECT_CONTEXT_SEARCH_PARAMETER, 'stored');
		}

		return `${url.pathname}${url.search}`;
	}

	public shouldPreserveSubjectContext(search: string = window.location.search): boolean {
		return new URLSearchParams(search).get(this.SUBJECT_CONTEXT_SEARCH_PARAMETER) === 'stored';
	}

	public getLoadingRoute() {
		const url = new URL(getAbsoluteRoute([this.routeBase, 'navigation', 'loading'].filter(Boolean).join('/')), window.location.origin);

		return `${url.pathname}${url.search}`;
	}

	public getPredefinedReasonForContactIds(search: string = window.location.search): number[] | null {
		return (
			new URLSearchParams(search)
				.get('rfv')
				?.split(',')
				.map((id) => safeNumberParse(id))
				.filter((id): id is number => id != null) ?? null
		);
	}

	public async getReasonForContacts(): AsyncResult<ClientConditionApiModel[]> {
		this.logger.logInformation('Get reason for contacts');

		const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
		if (unitConfigResult.err) {
			return Err(unitConfigResult.err);
		}

		if (unitConfigResult.ok.data.Offer?.HasConditionWorkflow === false) {
			return Ok([]);
		}

		const conditionsResult = await this.unitsApiService.getConditions(this.universalConfig.selectedUnitId);
		if (conditionsResult.err) {
			this.logger.logError(
				'User tried to get reason for contacts: {message} {statusCode}',
				conditionsResult.err.message,
				conditionsResult.err.statusCode,
			);

			return Err(conditionsResult.err);
		}

		return Ok(conditionsResult.ok.data);
	}

	public filterReasonForContacts(
		reasonForContacts: ClientConditionApiModel[],
		predefinedReasonForContactIds: number[] | null = this.getPredefinedReasonForContactIds(),
	): ClientConditionApiModel[] {
		if (predefinedReasonForContactIds == null) {
			return reasonForContacts;
		}

		if (predefinedReasonForContactIds.length === 0) {
			return [];
		}

		return reasonForContacts.filter((reasonForContact) => predefinedReasonForContactIds.includes(reasonForContact.Id));
	}

	public async startNavigation(payload: CareNavigationStartPayload): AsyncResult<CareNavigationStartResult> {
		const reasonForContact = payload.reasonForContact;
		const patientAge = this.getCurrentPatientAge();
		const numberParameters = {
			...(reasonForContact ? { ReasonForContactId: reasonForContact.Id } : {}),
			...(payload.sourceButtonId != null ? { ButtonId: payload.sourceButtonId } : {}),
			...(patientAge != null ? { Age: patientAge } : {}),
		};

		const navigationRequest = {
			InformationGatheringSubmissionId: payload.informationGatheringSubmissionId,
			DryRun: false,
			NumberParameters: numberParameters,
			StringParameters: {
				DescriptionOfMatter: payload.descriptionOfMatter,
			},
			Diagnoses: [],
			PresentingComplaints: reasonForContact
				? [
						{
							NodeId: reasonForContact.Id,
							Name: reasonForContact.Name ?? '',
						},
					]
				: [],
		} satisfies NavigateRequest;

		const startNavigationRequest = {
			AsUnverifiedRepresentativeForNin: payload.asUnverifiedRepresentativeForNin,
			NavigateRequest: navigationRequest,
		} satisfies StartNavigateRequest;

		try {
			const response = await NavigationService.navigationStart({
				client: clientV2,
				path: { receptionId: this.receptionId },
				body: startNavigationRequest,
			});

			if (response.data?.ExecuteActionStatus !== ExecuteActionStatus.Success) {
				return Err(new Error(`Navigation action failed with status: ${response.data?.ExecuteActionStatus}`));
			}

			// A null deeplink is expected for actions configured without one, e.g. an autoreply-only messaging action.
			const deepLink = response.data.Deeplink ?? null;

			sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
			sessionStorage.removeItem(this.DATE_OF_BIRTH_SESSION_STORAGE_KEY);

			return Ok({
				deepLink,
				isAbsolute: deepLink !== null && isAbsolute(deepLink),
			});
		} catch (error) {
			this.logger.logError('Navigation start failed: {error}', error);
			return Err(error instanceof Error ? error : new Error(String(error)));
		}
	}

	public trackReasonForContactSelection(reasonForContact: ClientConditionApiModel) {
		this.analyticService.trackEvent(AnalyticCategories.RedRobin, 'Select reason for contact', `${reasonForContact.Id}`);
	}
}

interface NavigateRequest {
	InformationGatheringSubmissionId?: string;
	DryRun: boolean;
	NumberParameters: Record<string, number>;
	StringParameters: Record<string, string>;
	Diagnoses: { NodeId: number; Probability: number }[];
	PresentingComplaints: { NodeId: number; Name: string }[];
}
